/**
 * Chemistry upload routing: filename normalization, legacy filename and test pairing, and which
 * registry key to use (test name only, then file name, then test name plus catalog column rules).
 * Parity with ChemUpload-deprecated.js where noted.
 */

/**
 * @typedef {{
 *   fileNameLower: string,
 *   selectedLower: string,
 *   selectedTestRule: object,
 * }} ChemUploadFileFirstRouteInput
 */

/** @typedef {{ match: (ctx: object) => boolean, key: string|((ctx: object) => string) }} ChemUploadRouteRule */

/** Substrings for moisture template tests (test name tier). */
const MOISTURE_TEMPLATE_TEST_SUBSTRINGS = [
  "moisture",
  "fat",
  "total solids",
  "fdb",
  "salt",
  "wps",
];

/** Substrings for legacy SSANN tests (test name tier). */
const LEGACY_SSANN_TEST_SUBSTRINGS = [
  "allergen",
  "anisidine",
  "sulfite",
  "nitrite",
  "scoville",
  "vitamin",
  "probiotic",
];

/** Substrings for legacy dynamic Excel tests (test name tier). */
const LEGACY_DYNAMIC_EXCEL_TEST_SUBSTRINGS = [
  "peroxide",
  "patulin",
  "water",
  "ph",
  "brix",
  "ffa",
  "aflatoxin",
];

/**
 * Factory for simple file name includes rules to reduce boilerplate.
 * @param {string} fragment
 * @param {string} key
 * @returns {ChemUploadRouteRule}
 */
function testNameIncludesRule(fragment, key) {
  return {
    match: (ctx) => ctx.testNameLower.includes(fragment),
    key,
  };
}

/**
 * Creates a rule that matches if the file name includes a given fragment. Used for file-first routing rules since
 * @param {string} fragment
 * @param {string} key
 * @returns {ChemUploadRouteRule}
 */
function fileNameIncludesRule(fragment, key) {
  return {
    match: (ctx) => ctx.fileNameLower.includes(fragment),
    key,
  };
}

/**
 * Compact token for Residual Solvents USP checks (strips spaces, underscores, hyphens).
 * Accepts "Residual Solvents -USP", "Residual Solvents USP", "residual_solvent_usp", etc.
 *
 * @param {string} value
 * @returns {string}
 */
export const normalizeResidualSolventsToken = (value) => (
  String(value || "").toLowerCase().replace(/[\s_-]+/g, "")
);

/**
 * True when a file or test name refers to Residual Solvents USP (singular/plural; hyphen optional).
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isResidualSolventsUspName = (value) => {
  const compact = normalizeResidualSolventsToken(value);
  return (
    (compact.includes("residualsolvents") || compact.includes("residualsolvent"))
    && compact.includes("usp")
  );
};

/**
 * Compiles specs that only have test name includes checks into ChemUploadRouteRules.
 * @param {Array<ChemUploadRouteRule|{ testIncludes: string, key: string }|{ match: ChemUploadRouteRule["match"], key: ChemUploadRouteRule["key"] }>} specs
 * @returns {ChemUploadRouteRule[]}
 */
function compileNameOnlySpecs(specs) {
  return specs.map((spec) => (
    spec.match
      ? spec
      : testNameIncludesRule(spec.testIncludes, spec.key)
  ));
}

/** @param {typeof CHEM_UPLOAD_NAME_AND_COLUMN_SPECS} specs @returns {ChemUploadRouteRule[]} */
function compileNameAndColumnSpecs(specs) {
  return specs.map((spec) => (
    spec.match
      ? spec
      : testNameIncludesRule(spec.testIncludes, spec.key)
  ));
}

/** Order matters: Agilent before plain metals; name checks before ML. */
const CHEM_UPLOAD_NAME_ONLY_SPECS = [
  { testIncludes: "metals agilent", key: "metalsAgilent" },
  { testIncludes: "fap", key: "fap" },
  { testIncludes: "residual solvents", key: "residualSolvents" },
  { testIncludes: "metals", key: "metals" },
  { match: (ctx) => ctx.isMl, key: "ml" },
];

/** Specific tests before generic legacy/dynamic parser branches. */
const CHEM_UPLOAD_NAME_AND_COLUMN_SPECS = [
  { testIncludes: "silver nitrate", key: "silverNitrate" },
  {
    match: (ctx) => MOISTURE_TEMPLATE_TEST_SUBSTRINGS.some(
      (fragment) => ctx.testNameLower.includes(fragment),
    ),
    key: "moisture",
  },
  { testIncludes: "polyphenol", key: "polyphenols" },
  { testIncludes: "mycotoxin", key: "mycotoxin" },
  {
    match: (ctx) => (
      ctx.testNameLower.includes("titratable acidity")
      || ctx.testNameLower.includes("titratable")
    ),
    key: "titratableAcidity",
  },
  { testIncludes: "punicalagins", key: "punicalagins" },
  { testIncludes: "pesticide", key: "pesticides" },
  {
    match: (ctx) => (
      !ctx.hasResultTypeRule
      && LEGACY_SSANN_TEST_SUBSTRINGS.some(
        (fragment) => ctx.testNameLower.includes(fragment),
      )
    ),
    key: "legacyChemistrySsann",
  },
  {
    match: (ctx) => (
      ctx.hasColumnRule
      && !ctx.hasResultTypeRule
      && LEGACY_DYNAMIC_EXCEL_TEST_SUBSTRINGS.some(
        (fragment) => ctx.testNameLower.includes(fragment),
      )
    ),
    key: "legacyDynamicExcel",
  },
  {
    match: (ctx) => ctx.hasResultTypeRule && ctx.resultCellHasComma,
    key: "dynamicOneToMany",
  },
  {
    match: (ctx) => ctx.hasResultTypeRule && !ctx.resultCellHasComma,
    key: "dynamicOneToOne",
  },
];

/** File-first tier: order preserved exactly (first match wins when a file is present). */
const CHEM_UPLOAD_FILE_FIRST_RULES = [
  fileNameIncludesRule("allergen", "legacyChemistrySsann"),
  {
    match: (ctx) => ctx.fileNameLower.includes("metal"),
    key: (ctx) => (ctx.fileNameLower.includes("agilent") ? "metalsAgilent" : "metals"),
  },
  fileNameIncludesRule("punicalagins", "punicalagins"),
  fileNameIncludesRule("moisture", "moisture"),
  fileNameIncludesRule("polyphenols", "polyphenols"),
  fileNameIncludesRule("anisidine", "legacyChemistrySsann"),
  fileNameIncludesRule("sulfite", "legacyChemistrySsann"),
  {
    match: (ctx) => (
      (ctx.fileNameLower.includes("nitrite") || ctx.fileNameLower.includes("nitrate"))
      && !ctx.fileNameLower.includes("silver")
      && ctx.selectedTestRule.test === "Nitrite/Nitrate"
    ),
    key: "legacyChemistrySsann",
  },
  fileNameIncludesRule("scoville", "legacyChemistrySsann"),
  {
    match: (ctx) => (
      ctx.fileNameLower.includes("silver")
      && ctx.fileNameLower.includes("nitrate")
      && ctx.selectedLower === "silver nitrate"
    ),
    key: "silverNitrate",
  },
  {
    match: (ctx) => (
      ctx.fileNameLower.includes("titratable") && ctx.selectedLower === "titratable acidity"
    ),
    key: "titratableAcidity",
  },
  {
    match: (ctx) => (
      ctx.fileNameLower.includes("mycotoxin") && ctx.selectedLower === "mycotoxin"
    ),
    key: "mycotoxin",
  },
  fileNameIncludesRule("probiotic", "legacyChemistrySsann"),
  fileNameIncludesRule("fap", "fap"),
  {
    match: (ctx) => (
      isResidualSolventsUspName(ctx.fileNameLower)
      && isResidualSolventsUspName(ctx.selectedLower)
    ),
    key: "residualSolvents",
  },
  {
    match: (ctx) => (
      ctx.fileNameLower.includes("pesticide")
      && (ctx.selectedLower === "pesticides" || ctx.selectedLower === "pesticide")
    ),
    key: "pesticides",
  },
];

const CHEM_UPLOAD_TEST_NAME_ONLY_RULES = compileNameOnlySpecs(
  CHEM_UPLOAD_NAME_ONLY_SPECS,
);

const CHEM_UPLOAD_TEST_NAME_AND_COLUMN_RULES = compileNameAndColumnSpecs(
  CHEM_UPLOAD_NAME_AND_COLUMN_SPECS,
);

/**
 * Picks the first rule that matches the context.
 * @template T
 * @param {Array<{ match: (ctx: T) => boolean, key: string|((ctx: T) => string) }>} rules
 * @param {T} context
 * @returns {string|null}
 */
function pickFirstRuleMatch(rules, context) {
  const matchedRule = rules.find((rule) => rule.match(context));
  if (!matchedRule) {
    return null;
  }
  return typeof matchedRule.key === "function"
    ? matchedRule.key(context)
    : matchedRule.key;
}

/**
 * Builds context for rules that depend only on test name (metals, ML, FAP, Residual Solvents).
 * @param {object|null|undefined} selectedTestRule
 * @returns {{ testNameLower: string, isMl: boolean }|null}
 */
export const buildChemUploadTestNameOnlyContext = (selectedTestRule) => {
  if (!selectedTestRule?.test?.trim()) {
    return null;
  }
  return {
    testNameLower: selectedTestRule.test.trim().toLowerCase(),
    isMl: selectedTestRule.is_ml === 1,
  };
};

/**
 * Builds context for rules that depend on test name and presence of columns.
 * @param {object|null|undefined} selectedTestRule
 * @returns {{
 *   selectedTestRule: object,
 *   testNameLower: string,
 *   hasResultTypeRule: boolean,
 *   resultCellHasComma: boolean,
 *   hasColumnRule: boolean,
 * }|null}
 */
export const buildChemUploadTestNameAndColumnRulesContext = (selectedTestRule) => {
  if (!selectedTestRule) {
    return null;
  }
  const testNameLower = selectedTestRule.test?.trim()?.toLowerCase() || "";
  const hasResultTypeRule = selectedTestRule.result_type !== null
    && selectedTestRule.result_type !== "";
  const resultCellHasComma = String(selectedTestRule.result_cell).includes(",");
  const hasColumnRule = selectedTestRule.genistaid_cell !== null
    && selectedTestRule.genistaid_cell !== ""
    && selectedTestRule.result_cell !== null
    && selectedTestRule.result_cell !== "";
  return {
    selectedTestRule,
    testNameLower,
    hasResultTypeRule,
    resultCellHasComma,
    hasColumnRule,
  };
};

/**
 * Resolves registry key based on test name only (metals, ML, FAP, Residual Solvents).
 * Should be called before file-name and column-based rules since those are more expensive to compute.
 * @param {object|null|undefined} selectedTestRule
 * @returns {string|null}
 */
export const resolveMethodKeyFromTestNameOnly = (selectedTestRule) => {
  const testNameOnlyContext = buildChemUploadTestNameOnlyContext(selectedTestRule);
  if (!testNameOnlyContext) {
    return null;
  }
  return pickFirstRuleMatch(CHEM_UPLOAD_TEST_NAME_ONLY_RULES, testNameOnlyContext);
};

/**
 * Resolves registry key based on test name and presence of specific columns, with legacy exceptions.
 * Should be called after test-name-only and file-name-based rules since those are more expensive to compute.
 * @param {object|null|undefined} selectedTestRule
 * @returns {string|null}
 */
export const resolveMethodKeyFromTestNameAndColumnRules = (selectedTestRule) => {
  const nameAndColumnsContext = buildChemUploadTestNameAndColumnRulesContext(selectedTestRule);
  if (!nameAndColumnsContext) {
    return null;
  }
  return pickFirstRuleMatch(CHEM_UPLOAD_TEST_NAME_AND_COLUMN_RULES, nameAndColumnsContext);
};

/** @param {ChemUploadFileFirstRouteInput} fileFirstRouteInput @returns {string|null} */
export const matchFileFirstRegistryKey = (fileFirstRouteInput) => pickFirstRuleMatch(CHEM_UPLOAD_FILE_FIRST_RULES, fileFirstRouteInput);

/**
 * @param {string} fileName
 * @returns {string}
 */
export const normalizeFileName = (fileName) => String(fileName).toLowerCase();

/**
 * Resolves which registry entry to run: test name only (metals / FAP / ML), then file name,
 * then test name + catalog column rules (resolveMethodKeyFromTestNameAndColumnRules).
 *
 * @param {{ file?: File|null, selectedTest: object|null }} params
 * @returns {string|null}
 */
export const resolveChemUploadMethodKeyFromFileAndTest = ({
  file = null,
  selectedTest: selectedTestRule = null,
} = {}) => {
  if (!selectedTestRule?.test?.trim()) {
    return null;
  }
  // Test name only rules are more expensive to compute, so we call them first.
  const testNameOnlyKey = resolveMethodKeyFromTestNameOnly(selectedTestRule);
  if (testNameOnlyKey) {
    return testNameOnlyKey;
  }
  // File name rules are more expensive to compute, so we call them second.
  const fileNameLower = file?.name ? normalizeFileName(file.name) : "";
  if (!fileNameLower) {
    return resolveMethodKeyFromTestNameAndColumnRules(selectedTestRule);
  }
  // File-first rules are more expensive to compute, so we call them third.
  const fromFile = matchFileFirstRegistryKey({
    fileNameLower,
    selectedLower: String(selectedTestRule.test).trim().toLowerCase(),
    selectedTestRule,
  });
  if (fromFile) {
    return fromFile;
  }

  // If no rules match, fall back to test name + column rules.
  return resolveMethodKeyFromTestNameAndColumnRules(selectedTestRule);
};

/**
 * When the selected test label and Excel file name do not match literally, legacy upload still
 * accepted specific pairs (moisture workbook + Fat / Salt, water file + Water Activity, etc.).
 * ChemUpload should call this from file validation alongside `fileName.includes(selectedTest)`.
 *
 * Mirrors ChemUpload-deprecated.js onHandleXlsDrop and isValidFiles.
 *
 * @param {{ test?: string, is_ml?: number }|null|undefined} selectedTestRule
 * @param {string} rawFileName
 * @returns {boolean}
 */
export const isLegacyChemUploadFileNamePairAllowed = (selectedTestRule, rawFileName) => {
  const testName = String(selectedTestRule?.test).trim();
  if (!testName) {
    return false;
  }
  const selectedTestNameLower = testName.toLowerCase();
  const fileNameLower = normalizeFileName(rawFileName);
  if (!fileNameLower) {
    return false;
  }
  if (fileNameLower.includes(selectedTestNameLower)) {
    return true;
  }

  if (fileNameLower.includes("water") && selectedTestNameLower === "water activity") {
    return true;
  }

  const moistureCompanionTests = ["fat", "total solids", "fdb", "salt", "wps"];
  if (fileNameLower.includes("moisture") && moistureCompanionTests.includes(selectedTestNameLower)) {
    return true;
  }

  if (fileNameLower.includes("metal") && !fileNameLower.includes("agilent") && selectedTestNameLower === "metals") {
    return true;
  }
  if (fileNameLower.includes("agilent") && selectedTestNameLower === "metals agilent") {
    return true;
  }

  if (fileNameLower.includes("punicalagins") && selectedTestNameLower.includes("punicalagin")) {
    return true;
  }
  if (fileNameLower.includes("polyphenols") && selectedTestNameLower === "total polyphenols") {
    return true;
  }

  if (
    (fileNameLower.includes("nitrite") || fileNameLower.includes("nitrate"))
    && !fileNameLower.includes("silver")
    && selectedTestNameLower === "nitrite/nitrate"
  ) {
    return true;
  }

  if (fileNameLower.includes("vitamins") && selectedTestNameLower === "vitamin fat soluble") {
    return true;
  }
  if (fileNameLower.includes("vitamin") && selectedTestNameLower === "vitamin water soluble") {
    return true;
  }

  if (fileNameLower.includes("scoville") && selectedTestNameLower === "scoville heat") {
    return true;
  }
  if (fileNameLower.includes("titratable") && selectedTestNameLower === "titratable acidity") {
    return true;
  }
  if (fileNameLower.includes("probiotic") && selectedTestNameLower === "probiotic") {
    return true;
  }
  if (fileNameLower.includes("fap") && selectedTestNameLower === "fap") {
    return true;
  }
  // Filename must include Residual Solvents USP (hyphen optional), e.g. "Residual Solvents USP.xlsx".
  if (
    isResidualSolventsUspName(fileNameLower)
    && isResidualSolventsUspName(selectedTestNameLower)
  ) {
    return true;
  }

  if (
    fileNameLower.includes("silver")
    && fileNameLower.includes("nitrate")
    && selectedTestNameLower === "silver nitrate"
  ) {
    return true;
  }
  if (fileNameLower.includes("mycotoxin") && selectedTestNameLower === "mycotoxin") {
    return true;
  }
  if (fileNameLower.includes("pesticide") && (selectedTestNameLower === "pesticides" || selectedTestNameLower === "pesticide")) {
    return true;
  }

  return false;
};
